import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Gavel, Landmark, ScrollText } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { AsOf } from "../components/AsOf";

const Card = ({ children, testId }) => (
  <div className="border border-zinc-800 bg-[#0b0b0b] p-6 md:p-8" data-testid={testId}>
    {children}
  </div>
);

const Primary = ({ url, label = "Read the primary text" }) => (
  <a
    href={url}
    target="_blank"
    rel="noreferrer"
    className="mono mt-5 inline-flex items-center gap-2 text-[0.56rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:text-white"
  >
    {label} <ExternalLink size={11} />
  </a>
);

export default function SportsLaw() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "The law around sport in Kenya — the Sports Act, the Bills and the case law",
    description:
      "The Sports Act 2013 and the statutes in force, the Sports Bill and Sports (Amendment) Bill 2026 before Parliament, the Awuonda judgment on registering a football club, and what each one asks of anyone building the game in Kenya.",
  });

  useEffect(() => {
    api.get("/sports-law")
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load this page. Refresh to try again."));
  }, []);

  useEffect(() => {
    if (!d || !window.location.hash) return;
    const el = document.querySelector(window.location.hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [d]);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="law-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="law-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="sports-law-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Governance</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.title}</h1>
          <p className="body-copy mt-7 max-w-3xl">{d.standfirst}</p>
          <AsOf
            testId="law-as-of"
            kind="official"
            asOf={d.as_at}
            publisher="Kenya Law, Parliament of Kenya, the Ministry and ADAK — read against the primary texts"
          />
        </Reveal>

        <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="law-counts">
          {[
            ["Statutes in force", d.counts.in_force, "Constitution to the Finance Act"],
            ["Before Parliament", d.counts.before_parliament, "Bills that would change the game"],
            ["Cases that tested it", d.counts.case_law, "High Court and the Tribunal"],
            ["What it asks of us", d.counts.compliance, "Steps in the build schedule"],
          ].map(([label, value, note], i) => (
            <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`law-count-${i}`}>
              <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
              <p className="mt-4 font-display text-3xl font-bold leading-none text-white md:text-4xl">{value}</p>
              <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 border-l-2 border-ochre bg-[#0b0b0b] p-6 md:p-8" data-testid="law-why">
          <p className="overline text-ochre">Why this sits on an investment site</p>
          <p className="mt-4 max-w-4xl whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.why}</p>
        </div>

        <section className="mt-14" data-testid="law-in-force">
          <div className="flex items-center gap-3">
            <Landmark size={16} className="text-ochre" />
            <h2 className="display-3">The Law As It Stands</h2>
          </div>
          <div className="mt-6 space-y-px bg-zinc-800">
            {d.in_force.map((item) => (
              <div key={item.key} className="bg-[#0b0b0b] p-6 md:p-8" data-testid={`law-statute-${item.key}`}>
                <p className="font-display text-xl font-bold uppercase leading-tight text-white md:text-2xl">
                  {item.title}
                </p>
                <p className="mono mt-3 text-[0.56rem] uppercase leading-relaxed tracking-[0.14em] text-ochre">
                  {item.instrument}
                </p>
                <div className="mt-5 grid gap-6 lg:grid-cols-2">
                  <p className="text-sm leading-relaxed text-zinc-300">{item.what}</p>
                  <p className="border-l-2 border-maasai pl-5 text-sm leading-relaxed text-zinc-400">
                    <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-maasai">
                      What it means for this plan
                    </span>
                    <span className="mt-2 block">{item.why}</span>
                  </p>
                </div>
                <Primary url={item.url} label={`${item.publisher} — read the text`} />
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="law-bills">
          <div className="flex items-center gap-3">
            <ScrollText size={16} className="text-ochre" />
            <h2 className="display-3">Before Parliament</h2>
          </div>
          <p className="body-copy mt-5 max-w-3xl">
            Three instruments in motion, any one of which changes how a club is registered, how integrity is
            policed and who regulates the game. A plan written to today&rsquo;s law alone would be out of date
            before the first court is poured.
          </p>
          <div className="mt-6 grid gap-px bg-zinc-800 lg:grid-cols-3">
            {d.before_parliament.map((bill) => (
              <div key={bill.key} className="bg-[#0b0b0b] p-6 md:p-7" data-testid={`law-bill-${bill.key}`}>
                <p className="font-display text-lg font-bold uppercase leading-tight text-white md:text-xl">
                  {bill.title}
                </p>
                <p className="mono mt-4 border-l-2 border-ochre pl-4 text-[0.56rem] uppercase leading-relaxed tracking-[0.14em] text-ochre">
                  {bill.stage}
                </p>
                <p className="mono mt-3 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                  {bill.sponsor}
                </p>
                <p className="mt-5 text-sm leading-relaxed text-zinc-300">{bill.what}</p>
                <p className="mt-4 text-xs leading-relaxed text-zinc-400">{bill.why}</p>
                <Primary url={bill.url} label={`${bill.publisher} — follow the Bill`} />
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="law-cases">
          <div className="flex items-center gap-3">
            <Gavel size={16} className="text-ochre" />
            <h2 className="display-3">Cases That Have Tested It</h2>
          </div>
          <div className="mt-6 space-y-px bg-zinc-800">
            {d.case_law.map((c) => (
              <div key={c.key} id={`case-${c.key}`} className="scroll-mt-40 bg-[#0b0b0b] p-6 md:p-8" data-testid={`law-case-${c.key}`}>
                <p className="font-display text-xl font-bold uppercase leading-tight text-white md:text-2xl">
                  {c.title}
                </p>
                <p className="mono mt-3 text-[0.56rem] uppercase leading-relaxed tracking-[0.14em] text-ochre">
                  {c.citation}
                </p>
                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  <div>
                    <p className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">The facts</p>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-300">{c.facts}</p>
                  </div>
                  <div>
                    <p className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Held</p>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-300">{c.held}</p>
                  </div>
                </div>
                <p className="mt-6 border-l-2 border-maasai pl-5 text-sm leading-relaxed text-zinc-400">
                  <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-maasai">
                    Why it matters here
                  </span>
                  <span className="mt-2 block">{c.why}</span>
                </p>
                <div className="flex flex-wrap gap-6">
                  <Primary url={c.url} label={`${c.publisher} — read the judgment`} />
                  {c.reported ? <Primary url={c.reported} label="As reported in the press" /> : null}
                  {c.player_slug ? (
                    <Link
                      to={`/hall-of-fame/${c.player_slug}`}
                      data-testid={`law-case-player-${c.key}`}
                      className="mono mt-5 inline-flex items-center gap-2 text-[0.56rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:text-white"
                    >
                      The petitioner is in our Hall of Fame <ArrowRight size={11} />
                    </Link>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="law-compliance">
          <h2 className="display-3 border-b border-white/15 pb-4">What The Law Asks Of This Plan</h2>
          <p className="body-copy mt-5 max-w-3xl">
            Eight requirements that belong in the build schedule rather than the appendix. Each one has a
            timetable, and several of them run longer than the construction they sit beside.
          </p>
          <div className="mt-6 grid gap-px bg-zinc-800 md:grid-cols-2">
            {d.compliance.map((c) => (
              <div key={c.step} className="bg-[#0b0b0b] p-6 md:p-7" data-testid={`law-step-${c.step}`}>
                <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-zinc-600">{c.step}</p>
                <p className="mt-3 font-display text-lg font-bold uppercase leading-tight text-white md:text-xl">
                  {c.heading}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12">
          <Card testId="law-disclaimer">
            <p className="overline text-ochre">Not legal advice</p>
            <p className="mt-4 max-w-4xl text-sm leading-relaxed text-zinc-400">{d.disclaimer}</p>
          </Card>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/plans" className="btn-primary" data-testid="law-plans-link">
            The recommended steps <ArrowRight size={14} />
          </Link>
          <Link to="/fkf-feed" className="btn-ghost" data-testid="law-feed-link">
            Federation news and fixtures
          </Link>
          <Link to="/legal" className="btn-ghost" data-testid="law-legal-link">
            Our own waiver and disclaimer
          </Link>
        </div>
      </div>
    </div>
  );
}
